import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, ListFilter } from "lucide-react";
import BottomBar from "../../components/bottomBar/BottomBar";
import HomeSlider from "../../components/Sliders/HomeSlider";
import TopBar from "../../components/topBar/TopBar";
import PageLayout from "../layout/PageLayout";
import Article from "../../components/Articles/Article";
import ShareProduct from "../../components/Modals/ShareProduct";
import SampleSection from "../../components/SampleSection";
import WhyChoose from "../../components/Sections/WhyChoose";
import ProductService from "../../services/productService";
import usePricing from "../../hooks/usePricing";
import { mapApiProductToArticle, toProductList } from "../../utils/productCard";

const ArticleSkeleton = () => (
  <div className="w-full bg-white rounded-xl overflow-hidden border border-gray-100 animate-pulse">
    <div className="aspect-square bg-gray-100" />
    <div className="p-2 space-y-2">
      <div className="h-3 bg-gray-100 rounded w-3/4" />
      <div className="h-3 bg-gray-100 rounded w-1/2" />
    </div>
  </div>
);

const SectionHeader = ({ title, to, children }) => (
  <div className="flex items-center justify-between gap-2 px-3 pt-4 pb-1">
    <h2 className="text-[17px] font-bold text-gray-900">{title}</h2>
    {children}
    {to && (
      <Link to={to} className="text-xs text-orange-500 font-medium">
        Voir plus
      </Link>
    )}
  </div>
);

/** Produits demandés par appel : de quoi remplir un écran et un peu plus. */
const PAGE_SIZE = 12;

/** Le sentinelle déclenche le chargement avant d'être réellement visible. */
const LOAD_AHEAD_MARGIN = "400px";

/** Les trois lectures possibles de « Bonnes opportunités ». */
const OPPORTUNITY_FILTERS = [
  { value: "all", label: "Tout" },
  { value: "business", label: "Business" },
  { value: "deals", label: "Bons plans" },
];

/**
 * Filtre en icône : les trois pastilles mangeaient la ligne du titre sur les
 * écrans étroits. Le choix courant se lit au point orange, le détail dans le
 * menu — un filtre se consulte moins souvent qu'il ne s'applique.
 */
const OpportunityFilter = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const active = OPPORTUNITY_FILTERS.find((f) => f.value === value) || OPPORTUNITY_FILTERS[0];
  const isFiltered = value !== "all";

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`relative flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
          open || isFiltered ? "bg-orange-50 text-orange-500" : "bg-gray-100 text-gray-500"
        }`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Filtrer les opportunités (${active.label})`}
      >
        <ListFilter className="w-4 h-4" />
        {isFiltered && (
          <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-orange-500" />
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-30 min-w-[140px] rounded-xl border border-gray-100 bg-white shadow-lg overflow-hidden"
        >
          {OPPORTUNITY_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              role="menuitemradio"
              aria-checked={value === f.value}
              onClick={() => {
                onChange(f.value);
                setOpen(false);
              }}
              className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-[13px] transition-colors ${
                value === f.value ? "text-orange-600 font-semibold" : "text-gray-600"
              }`}
            >
              {f.label}
              {value === f.value && <Check className="w-3.5 h-3.5 shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Home = () => {
  const location = useLocation();
  const [activeShare, setActiveShare] = useState(false);
  const [product, setProduct] = useState(null);
  const [apiProducts, setApiProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [opportunityFilter, setOpportunityFilter] = useState("all");
  const { pricing } = usePricing();

  const toggleActiveShare = (selected) => {
    setActiveShare((prev) => !prev);
    setProduct(selected);
  };

  // Défilement infini : on charge la page suivante quand le bas de la grille
  // approche de l'écran, pas avant. Les refs portent l'état que l'observateur
  // lit à l'instant T — un état React serait périmé dans son callback.
  const requestRef = useRef(0);
  const pageRef = useRef(0);
  const loadingRef = useRef(false);
  const sentinelRef = useRef(null);

  // Le filtre part à l'API : filtrer côté client ne verrait que les pages déjà
  // chargées, « Business » pourrait afficher trois produits alors qu'il y en a dix.
  const fetchPage = useCallback(
    async (pageNumber) => {
      const ticket = requestRef.current;
      const params = { paginate: PAGE_SIZE, page: pageNumber };
      if (opportunityFilter === "business") params.is_business_offer = 1;
      if (opportunityFilter === "deals") params.is_bon_plan = 1;

      loadingRef.current = true;
      try {
        const data = await ProductService.getProductsPage(params);
        // Le filtre a changé pendant l'appel : cette réponse est périmée.
        if (ticket !== requestRef.current) return;

        // `GET /products` charge déjà `images` en plus de `default_image` :
        // aucun appel par produit n'est nécessaire, et `mapApiProductToArticle`
        // retombe sur `images` quand aucune n'est marquée par défaut.
        const raw = toProductList(data);
        const items = raw
          .map((item) => mapApiProductToArticle(item, { preferSample: opportunityFilter === "deals" }))
          .filter(Boolean);
        setApiProducts((prev) => (pageNumber === 1 ? items : [...prev, ...items]));
        pageRef.current = pageNumber;

        // `meta.last_page` dit s'il reste des pages ; sans lui, une page pleine
        // en laisse supposer une autre. Ne jamais retomber sur « 1 » : c'est ce
        // qui bloquait le défilement après la première page.
        const lastPage = Number(data?.meta?.last_page);
        setHasMore(lastPage > 0 ? pageNumber < lastPage : raw.length >= PAGE_SIZE);
      } catch (err) {
        console.error("Erreur lors du chargement des produits:", err);
        if (ticket === requestRef.current) setHasMore(false);
      } finally {
        if (ticket === requestRef.current) {
          loadingRef.current = false;
          setLoadingProducts(false);
          setLoadingMore(false);
        }
      }
    },
    [opportunityFilter]
  );

  // Changement de filtre (et premier rendu) : on repart de la première page.
  useEffect(() => {
    requestRef.current += 1;
    pageRef.current = 0;
    setApiProducts([]);
    setHasMore(false);
    setLoadingMore(false);
    setLoadingProducts(true);
    fetchPage(1);
  }, [fetchPage]);

  // Se réarme à chaque page reçue : si l'écran est assez haut pour que le
  // sentinelle reste visible, l'observateur se redéclenche et charge la suite.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!hasMore || !node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || loadingRef.current) return;
        setLoadingMore(true);
        fetchPage(pageRef.current + 1);
      },
      { rootMargin: `0px 0px ${LOAD_AHEAD_MARGIN} 0px` }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, apiProducts.length, fetchPage]);

  // Ancre #produits-normaux (ex. depuis la page Affiliation)
  useEffect(() => {
    if (location.hash !== "#produits-normaux") return undefined;
    const scrollToProducts = () => {
      const el = document.getElementById("produits-normaux");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    // Attendre le rendu des produits / layout
    const t = window.setTimeout(scrollToProducts, loadingProducts ? 400 : 80);
    return () => window.clearTimeout(t);
  }, [location.hash, loadingProducts]);

  return (
    // `className` blanc : le fond de l'application est beige, et la réserve
    // laissée à la barre du bas laissait voir une bande rose sous la grille.
    <PageLayout bottomBar={<BottomBar />} topBar={<TopBar />} className="bg-white">
      <div className="bg-white min-h-screen">
        <ShareProduct
          product={product}
          toggleActiveShare={toggleActiveShare}
          activeShare={activeShare}
        />

        {/* Slider */}
        <HomeSlider />

        {/* Pourquoi Chinexpress (livraison + arguments) */}
        <WhyChoose />

        {/* Stock Local (échantillons) */}
        <SectionHeader title="Stock Local" to="/listEchantillon" />
        <SampleSection hideHeader />

        {/* Toutes les opportunités : offres business et produits normaux dans
            la même grille, les premières en tête. */}
        <div id="produits-normaux" className="scroll-mt-20">
          <SectionHeader title="Bonnes opportunités">
            <OpportunityFilter value={opportunityFilter} onChange={setOpportunityFilter} />
          </SectionHeader>
          {/* `pb`, pas `mb` : une marge basse s'échappe du bloc blanc par
              fusion des marges et laisse une bande de fond nu sous la grille.
              La barre du bas a déjà sa réserve dans PageLayout (`pb-[75px]`),
              il ne reste ici qu'à faire respirer le dernier produit. */}
          {/* Masonry à deux colonnes indépendantes : chaque colonne se remplit de
              son côté, donc une carte plus courte laisse la suivante remonter
              se coller dessous au lieu de laisser un vide (une grille aligne
              par rangées). Répartition en alternance — 1er à gauche, 2e à
              droite… — plutôt que `columns-2` : un produit chargé plus tard
              se pose en bas et ne déplace jamais ceux déjà affichés. */}
          {apiProducts.length === 0 && !loadingProducts ? (
            <p className="mx-3 mt-3 pb-4 text-center text-gray-400 py-8 text-sm">
              Aucun produit disponible
            </p>
          ) : (
            <div className="mx-3 mt-3 pb-4 flex gap-3 items-start">
              {[0, 1].map((column) => (
                <div key={column} className="flex-1 min-w-0 flex flex-col gap-3">
                  {loadingProducts
                    ? Array.from({ length: 3 }).map((_, i) => <ArticleSkeleton key={i} />)
                    : apiProducts
                        .filter((_, index) => index % 2 === column)
                        .map((produit) => (
                          <Article
                            key={produit.id}
                            product={produit}
                            pricing={pricing}
                            share={() => toggleActiveShare(produit)}
                            showBonPlanBadge={produit.isBonPlan}
                          />
                        ))}
                  {loadingMore && <ArticleSkeleton />}
                </div>
              ))}
            </div>
          )}
          {/* Sentinelle : dès qu'il approche de l'écran, la page suivante part. */}
          {hasMore && <div ref={sentinelRef} aria-hidden="true" className="h-px" />}
        </div>
      </div>
    </PageLayout>
  );
};

export default Home;
